import * as React from 'react';
import { useState, useEffect } from 'react';
import EventBus from '../EventBus';
import EnergipresentationAccordion from './EnergipresentationAccordion';
import iEnergipresentationScalesModel from './iEnergipresentationScales';
import iEnergipresentationGraphPropsModel from './iEnergipresentationGraphProps';
import iEnergipresentationApiRequestModel from "./RequestModels/iEnergipresentationApiRequest";
import iScaleData from './ResponseModels/iScaleData';
import iScalesItemModel from './ResponseModels/iScalesItem';
import iEnergipresentationScalesResponse from './iEnergipresentationScalesResponse';
import iEnergipresentationYearsModel from './iEnergipresentationYears';
import iEnergipresentationMinMaxYearsModel from './iEnergipresentationMinMaxYears';
import DataHelper from './DataHelper';
import DisplayHelper from './DisplayHelper';
import EnergipresentationExportButton from './EnergipresentationExportButton';
import EnergipresentationOpenInNewTabButton from './EnergipresentationOpenInNewTabButton';


const EnergipresentationScales = (props: iEnergipresentationGraphPropsModel) => {
    const initialState: iEnergipresentationScalesModel = {
        ...props,
        isLoading: true,
        year: "0",
        scaleData: {} as iScaleData,
    };

    const [delayed, setDelayed] = useState(true);
    const [state, setState] = useState<iEnergipresentationScalesModel>(initialState);

    useEffect(() => {
        setYears();

        EventBus.on("UpdateBlockYears_" + state.blockId, updateSelectedYears);
    }, []);

    useEffect(() => {
        const timeout = setTimeout(() => setDelayed(false), 3000);
        return () => clearTimeout(timeout);
    }, []);

    const updateSelectedYears = async (years: iEnergipresentationYearsModel, data: iEnergipresentationScalesResponse = null) => {
        if (!hasValues(state.sourceAEltillforselValues) && !hasValues(data?.sourceAEltillforselValues) &&
            !hasValues(state.sourceBEnergianvandningValues) && !hasValues(data?.sourceBEnergianvandningValues) &&
            !hasValues(state.sourceCEnergianvandningBranslenValues) && !hasValues(data?.sourceCEnergianvandningBranslenValues)) {

            let customNewYears: iEnergipresentationYearsModel = { firstYear: years.firstYear };
            await fetchScaleData(customNewYears, updateSelectedYearFromApi);
            return;
        }

        let isLoading = false;
        if (!hasValues(state.sourceAEltillforselValues) && hasValues(data?.sourceAEltillforselValues) &&
            !hasValues(state.sourceBEnergianvandningValues) && hasValues(data?.sourceBEnergianvandningValues) &&
            !hasValues(state.sourceCEnergianvandningBranslenValues) && hasValues(data?.sourceCEnergianvandningBranslenValues)) {

            data.year = years.firstYear.toString();
            updateState(data, isLoading);
            return;
        }

        let newState = { ...state };
        newState.isLoading = isLoading;
        setState(newState);
    };

    const hasValues = (list: Array<any>): boolean => {
        return list !== undefined && list !== null && list.length > 0;
    };

    const updateSelectedYearFromApi = async (data: iEnergipresentationScalesResponse, years: iEnergipresentationYearsModel) => {
        await updateSelectedYears(years, data);
    };

    const fetchScaleData = async (newYears: iEnergipresentationYearsModel, callback: Function) => {
        let apiRequest: iEnergipresentationApiRequestModel = {
            blockId: state.blockId,
            lastPublished: state.lastPublished
        };

        await DataHelper.FetchGraphData(apiRequest, callback, newYears);
    };

    const initializeScale = (data: iEnergipresentationScalesResponse) => {
        if (!hasValues(data?.sourceAEltillforselValues) || !hasValues(data?.sourceBEnergianvandningValues) || !hasValues(data?.sourceCEnergianvandningBranslenValues)) {
            EventBus.dispatch("ToggleBlock_" + state.blockId, null);
        }

        let newState = updateState(data);

        let minYearKey = newState.availableYears[0];
        let maxYearKey = newState.availableYears[newState.availableYears.length - 1];
        let minYear: number = minYearKey !== undefined && minYearKey !== null ? parseInt(minYearKey) : -1;
        let maxYear: number = maxYearKey !== undefined && maxYearKey !== null ? parseInt(maxYearKey) : -1;
        let currentYears: iEnergipresentationMinMaxYearsModel = { minYear: minYear, maxYear: maxYear };
        EventBus.dispatch("LoadedBlock_" + newState.blockId, currentYears);
    };

    const updateState = (data: iEnergipresentationScalesResponse, isLoading: boolean = true): iEnergipresentationScalesModel => {
        var newState = { ...state };

        newState.scaleData.descriptionDifference = data.descriptionDifference;
        newState.scaleData.titleLeftSide = data.titleLeft;
        newState.scaleData.titleRightSide = data.titleRight;
        newState.scaleData.resultTextSourceB = data.resultTextB;
        newState.scaleData.resultTextSourceC = data.resultTextC;

        newState.sourceAEltillforselValues = mapValues(data.sourceAEltillforselValues, data.divideBy.find((o) => { return o.key === 'A' }).value, true);
        newState.sourceBEnergianvandningValues = mapValues(data.sourceBEnergianvandningValues, data.divideBy.find((o) => { return o.key === 'B' }).value);
        newState.sourceCEnergianvandningBranslenValues = mapValues(data.sourceCEnergianvandningBranslenValues, data.divideBy.find((o) => { return o.key === 'C' }).value);

        newState.yearIndex = data.availableYears.indexOf(data.year);

        newState.scaleData.sourceAValues = newState.sourceAEltillforselValues.map((a) => { return { key: a.key, value: [a.value[newState.yearIndex]] }; });
        newState.scaleData.sourceBValues = newState.sourceBEnergianvandningValues.map((a) => { return { key: a.key, value: [a.value[newState.yearIndex]] }; });
        newState.scaleData.sourceCValues = newState.sourceCEnergianvandningBranslenValues.map((a) => { return { key: a.key, value: [a.value[newState.yearIndex]] }; });
        newState.scaleData.sourceATotal = getTotal(newState.sourceAEltillforselValues, newState.yearIndex);
        newState.scaleData.sourceBTotal = getTotal(newState.sourceBEnergianvandningValues, newState.yearIndex);
        newState.scaleData.sourceCTotal = getTotal(newState.sourceCEnergianvandningBranslenValues, newState.yearIndex);

        newState.scaleData.sourceBAndCTotal = newState.scaleData.sourceBTotal + newState.scaleData.sourceCTotal;
        newState.scaleData.statisticDifference = newState.scaleData.sourceATotal - (newState.scaleData.sourceBAndCTotal);
        newState.scaleData.sourceRightSideTotal = newState.scaleData.sourceBAndCTotal + newState.scaleData.statisticDifference;

        newState.scaleData.year = data.year;
        newState.year = data.year;
        newState.availableYears = data.availableYears;

        newState.textReplacements = { FROM: newState.year };

        newState.headTitle = DisplayHelper.ReflectDataInText(newState.headTitle, newState.textReplacements);

        newState.isLoading = isLoading;
        setState(newState);
        if (delayed && newState.scaleData.sourceRightSideTotal) {
            EventBus.dispatch("GraphDataLoaded", newState.blockId);
        }
        return newState;
    };

    const mapValues = (values: iScalesItemModel[], divideBy: number, sort: boolean = false): iScalesItemModel[] => {
        var mapped = values.map((item: iScalesItemModel) => {
            return { ...item, value: item.value.map(v => Math.round(v / divideBy)) };
        });

        if (!sort) {
            return mapped;
        }

        return mapped.sort((itemA, itemB) => {
            if (itemA.key === "Solkraft") {
                return -1;
            } else if (itemB.key === "Solkraft") {
                return 1;
            } else {
                return 0;
            }
        });
    };
    const getTotal = (values: iScalesItemModel[], yearIndex: number): number => {
        if (yearIndex < 0) {
            return 0;
        }

        return values.reduce((accumulator, currentItem) => {
            return accumulator + currentItem.value[yearIndex];
        }, 0);
    };

    const setYears = async (newYears?: iEnergipresentationYearsModel) => {
        if (newYears === undefined || newYears === null) {
            newYears = { firstYear: -1, secondYear: -1 };
        }

        await fetchScaleData(newYears, initializeScale);
    };

    const hasAllValues = (data: any): boolean => {
        if (!data) { return false; }
        if (typeof data === 'number') { return true; }
        if (typeof data === 'string') { return data.length > 0; }
        if (Array.isArray(data)) {
            if (!data.length) { return false; }
            let i = 0;
            for (let item in data) {
                if (!hasAllValues(item,)) { return false; };
            }
            return true;
        }

        if (!hasValues(Object.keys(data))) { return false; }
        for (let key of Object.keys(data)) {
            if (!hasAllValues(data[key])) { return false; }
        }
        return true;
    };

    const addSpecificHelpText = (cmsText: string): string => {
        if (!hasAllValues(state.scaleData) || !cmsText) {
            return cmsText;
        }
        let result = DisplayHelper.ReflectDataInText(cmsText, state.textReplacements);
        return result;
    };

    const getTableHeading = () => {
        return DisplayHelper.ReflectDataInText(state.helpColumnHeaders, state.textReplacements, true)?.split(DisplayHelper.DataDelimiter) ?? [];
    };

    const getTable = (): string => {
        if (!hasAllValues(state.scaleData)) {
            return '';
        }
        let heading = getTableHeading();
        const decimals = 0;
        const unit = 'TWh';
        return DisplayHelper.ToHTMLTable(state.scaleData, 'scale', state.headTitle, heading, decimals, unit, state.helpInvertTableAxis);
    };

    return (
        <React.Fragment>
            <h3>{state.headTitle}</h3>
            <div className="scale-wrapper">
                <div className="energipresentation-scale-container">
                    <div className="scroll-container">
                        <div className="image-container">
                            <img src="/Static/img/EnergipresentationScale.png" />
                            <div className="left-side">
                                <div className="title"><div>{state.scaleData.titleLeftSide}</div></div>
                                {hasValues(state.sourceAEltillforselValues) && (
                                    <React.Fragment>
                                        {state.sourceAEltillforselValues.map((item, itemIndex) => {
                                            return (
                                                <div key={"sourceA-Item-" + itemIndex} className={"box-text box-" + itemIndex}><div>{item.key}&nbsp;<span className="span-value">{item.value[state.yearIndex]}</span></div></div>
                                            )
                                        })}
                                    </React.Fragment>
                                )}
                                <div className="box-text left-total"><p>{state.scaleData.sourceATotal}</p></div>
                            </div>
                            <div className="right-side">
                                <div className="title"><div>{state.scaleData.titleRightSide}</div></div>
                                <div className="box-text difference-box"><div><span className="span-value">{state.scaleData.statisticDifference}*</span></div></div>
                                {hasValues(state.sourceCEnergianvandningBranslenValues) && (
                                    <React.Fragment>
                                        {state.sourceCEnergianvandningBranslenValues.map((item, itemIndex) => {
                                            return (
                                                <div key={"sourceC-Item-" + itemIndex} className={"box-text box-" + (itemIndex)}><div>{item.key}&nbsp;<span className="span-value">{item.value[state.yearIndex]}</span></div></div>
                                            )
                                        })}
                                    </React.Fragment>
                                )}
                                {hasValues(state.sourceBEnergianvandningValues) && (
                                    <React.Fragment>
                                        {state.sourceBEnergianvandningValues.map((item, itemIndex) => {
                                            return (
                                                <div key={"sourceB-Item-" + itemIndex} className={"box-text box-" + (itemIndex + state.sourceCEnergianvandningBranslenValues.length)}><div>{item.key}&nbsp;<span className="span-value">{item.value[state.yearIndex]}</span></div></div>
                                            )
                                        })}
                                    </React.Fragment>
                                )}
                                <div className="box-text right-total"><p>{state.scaleData.sourceRightSideTotal}</p></div>
                            </div>
                            <div className="current-year-box"><p>{state.year}</p></div>
                            <div className="description-difference-box"><p>{state.scaleData.descriptionDifference}</p></div>
                        </div>
                        <div className="right-side-totals">
                            <div className="top-total">
                                <div className="text">
                                    {state.scaleData.resultTextSourceC}
                                    <div className="value">{state.scaleData.sourceCTotal} TWh</div>
                                </div>
                            </div>
                            <div className="bottom-total">
                                <div className="text">
                                    {state.scaleData.resultTextSourceB}
                                    <div className="value">{state.scaleData.sourceBTotal} TWh</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="energipresentation-buttons-container">
                <div className="button-wrapper">
                    <EnergipresentationOpenInNewTabButton
                        id={state.blockId}
                        data={state.scaleData}
                        title={state.headTitle}
                        viewType="scale"
                        textReplacements={state.textReplacements}
                        columnHeaders={state.helpColumnHeaders}
                        invertTableAxis={state.helpInvertTableAxis}
                        helpTitle={state.helpTitle}
                        helpText={addSpecificHelpText(state.helpText)}
                        table={getTable()}
                        modelStyle={state.helpModelStyle}
                    />
                    {((hasValues(state.sourceAEltillforselValues) && hasValues(state.sourceCEnergianvandningBranslenValues) && hasValues(state.sourceBEnergianvandningValues)) || delayed) && (
                        <EnergipresentationExportButton
                            series={state.scaleData}
                            type={'scale'}
                            title={state.headTitle}
                            heading={getTableHeading()}
                            unit={'TWh'}
                        />
                    )}
                </div>

            </div>

            {((hasValues(state.sourceAEltillforselValues) && hasValues(state.sourceCEnergianvandningBranslenValues) && hasValues(state.sourceBEnergianvandningValues)) || delayed) &&
                state.helpText && (
                    <EnergipresentationAccordion
                        key={"accordion_" + state.blockId}
                        title={state.helpTitle}
                        text={addSpecificHelpText(state.helpText)}
                        table={getTable()}
                        modelStyle={state.helpModelStyle}
                    />
                )}
        </React.Fragment >
    );
};
export default EnergipresentationScales;
